import classNames from 'classnames'
import { forwardRef, KeyboardEvent, RefObject, useEffect, useState } from 'react'

import { checkValue } from './helpers/check'
import { useFocused } from './helpers/useFocused'
import { InputWrap } from './InputWrapper'
import { IInputCommonProps } from './models/IInputCommonProps'

import styles from './Input.module.scss'

type Ref = HTMLInputElement

export interface INumberProps extends IInputCommonProps {
  ref?: RefObject<HTMLInputElement>
  /**
   * Callback function fires when key is pressed
   */
  onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void
  /**
   * Callback function fires when key is pressed
   */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  /**
   * Turn off browser number action buttons
   */
  noNumberSpinner?: boolean
  /**
   * Display text format
   */
  format?: 'decimal' | 'percent'
  /**
   * Use group separators
   */
  useGrouping?: boolean
  /**
   * Minimum value
   */
  min?: string | number
  /**
   * Maximum value
   */
  max?: string | number
  /**
   * Root test ID. This property is used for automated tests.
   */
  __testId?: string
}

export const NumberInput = forwardRef<Ref, INumberProps>(
  (
    {
      classes,
      margin,
      label,
      value,
      wrapper,
      disabled,
      startContent,
      endContent,
      dir,

      format = 'decimal',
      useGrouping = true,
      id,
      autoFocus,
      placeholder,
      autocompleteOff,
      min,
      max,
      onChange,
      onFocus,
      onBlur,
      onSelect,

      onKeyPress,
      onKeyDown,
      noNumberSpinner = true,
      __testId,
    },
    ref
  ) => {
    const { focused, setFocused } = useFocused({ disabled })
    const formatter = new Intl.NumberFormat('ru', {
      style: format,
      useGrouping,
    })

    const calcDisplayValue = () => (value || value === 0 ? formatter.format(Number(value)) : '')
    const prepareValue = () => {
      return value || value === 0 ? checkValue({ value: +value, min, max }) : ''
    }

    const [displayValue, setDisplayValue] = useState<string>(calcDisplayValue())

    useEffect(() => {
      if (!focused) {
        setDisplayValue(calcDisplayValue())
      }
    }, [value, focused, setDisplayValue])

    return (
      <InputWrap
        classes={classes}
        margin={margin}
        label={label}
        value={value}
        wrapper={wrapper}
        disabled={disabled}
        startContent={startContent}
        endContent={endContent}
        dir={dir}
        focused={focused}
      >
        <input
          data-testid={__testId || 'inputField'}
          dir={dir}
          autoComplete={autocompleteOff ? 'disable' : undefined}
          className={classNames(
            styles.control,
            {
              [styles['control--focused']]: label && (focused || value),
              [styles['control--noSpin']]: noNumberSpinner,
            },
            classes?.control
          )}
          id={id}
          ref={ref}
          type={focused ? 'number' : 'text'}
          onChange={(e) => onChange && onChange(e.target.value)}
          value={focused ? value : displayValue}
          disabled={disabled}
          onFocus={() => {
            setFocused(true)
            onFocus && onFocus()
          }}
          onBlur={(e: any) => {
            setDisplayValue(calcDisplayValue())
            const preparedValue = prepareValue()
            if (value !== preparedValue) {
              onChange && onChange(preparedValue)
            }
            setFocused(false)
            onBlur && onBlur(e)
          }}
          autoFocus={autoFocus}
          onSelect={onSelect}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          min={min}
          max={max}
        />
      </InputWrap>
    )
  }
)
